import React, { useEffect, useRef, useState } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Alert,
    Spinner,
    Accordion,
    Modal,
    ProgressBar,
    Toast,
    ToastContainer,
    Navbar,
    Nav,
    Offcanvas
} from 'react-bootstrap';
import {
    Download,
    Copy,
    Instagram,
    CheckCircle,
    AlertCircle,
    Info,
    Zap,
    Shield,
    Smartphone,
    Globe,
    Users,
    Clock,
    Star,
    HelpCircle,
    BookOpen,
    Search,
    Share2,
    Link,
    ArrowDown,
    Trash2,
    Menu,
    Wifi,
    Youtube,
    Video,
    User
} from 'lucide-react';

import { calculateBaseCount } from './constant';
import logo from './logo.png';
const ClipDart = () => {
    const [url, setUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [videoData, setVideoData] = useState(null);
    const [showInstructions, setShowInstructions] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [downloadCount, setDownloadCount] = useState(calculateBaseCount());
    const [progress, setProgress] = useState(0);
    const [showFAQ, setShowFAQ] = useState(false);
    const [showOffcanvas, setShowOffcanvas] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVariant, setToastVariant] = useState('success');
    const [showFeatures, setShowFeatures] = useState(false);
    const currentYear = new Date().getFullYear();
    const formRef = useRef(null);

    useEffect(() => {
        const scrollToForm = () => {
            if (formRef.current) {
                formRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        };

        // Delay scroll to ensure everything is rendered
        const timer = setTimeout(scrollToForm, 500);
        return () => clearTimeout(timer);
    }, []);
    // API Configuration
    const API_BASE_URL = 'https://api.clipdart.xyz/';
    // const API_BASE_URL = 'http://localhost:5006/';

    // URL validation for both YouTube and Instagram
    const isValidUrl = (url) => {
        if (!url || typeof url !== 'string') return false;

        const patterns = [
            // YouTube patterns
            /^https:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/).+/,
            /^https:\/\/(www\.)?youtube\.com\/(shorts|embed)\/.+/,

            // Instagram patterns
            /^https:\/\/(www\.)?instagram\.com\/reel\/[A-Za-z0-9_-]+/,
            /^https:\/\/(www\.)?instagram\.com\/reels\/[A-Za-z0-9_-]+/,
            /^https:\/\/(www\.)?instagram\.com\/p\/[A-Za-z0-9_-]+/,
            /^https:\/\/(www\.)?instagram\.com\/stories\/[A-Za-z0-9_.]+\/[A-Za-z0-9_-]+/,
            /^https:\/\/(www\.)?instagram\.com\/tv\/[A-Za-z0-9_-]+/,

            // Additional Instagram patterns for various URL formats
            /^https:\/\/(www\.)?instagram\.com\/[A-Za-z0-9_.]+\/reel\/[A-Za-z0-9_-]+/,
            /^https:\/\/(www\.)?instagram\.com\/[A-Za-z0-9_.]+\/p\/[A-Za-z0-9_-]+/
        ];

        return patterns.some(pattern => pattern.test(url.trim()));
    };

    // Detect platform
    const detectPlatform = (url) => {
        if (url.includes('youtube.com') || url.includes('youtu.be')) {
            return 'youtube';
        } else if (url.includes('instagram.com')) {
            return 'instagram';
        }
        return 'unknown';
    };

    // Extract video ID from URL
    const extractVideoId = (url) => {
        const platform = detectPlatform(url);
        if (platform === 'youtube') {
            const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/embed\/)([A-Za-z0-9_-]+)/);
            return match ? match[1] : 'Unknown';
        } else if (platform === 'instagram') {
            const match = url.match(/\/(?:reel|reels|p)\/([A-Za-z0-9_-]+)/);
            return match ? match[1] : 'Unknown';
        }
        return 'Unknown';
    };

    // Show toast notification
    const showToastNotification = (message, variant = 'success') => {
        setToastMessage(message);
        setToastVariant(variant);
        setShowToast(true);
    };

    // Call the download API
    const callDownloadAPI = async (videoUrl) => {
        try {
            const response = await fetch(`${API_BASE_URL}download?url=${encodeURIComponent(videoUrl)}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || `HTTP error! status: ${response.status}`);
            }

            return data;
        } catch (error) {
            console.error('API call failed:', error);
            throw error;
        }
    };

    // Direct video download function
    const downloadVideoDirectly = async (videoUrl, filename) => {
        try {
            setLoading(true);
            showToastNotification('Starting download...', 'info');

            const link = document.createElement('a');
            link.href = videoUrl;
            link.download = filename || `clipdart_video_${Date.now()}.mp4`;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';

            if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
                window.open(videoUrl, '_blank');
                showToastNotification('Video opened! Tap and hold to save', 'success');
            } else {
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                showToastNotification('Download started!', 'success');
            }

            setDownloadCount(prev => prev + 1);
        } catch (error) {
            console.error('Download failed:', error);
            showToastNotification('Download failed. Please try again.', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // Main download handler
    const handleDownload = async () => {
        // Input validation
        if (!url.trim()) {
            setError('Please enter a YouTube or Instagram video URL');
            return;
        }

        if (!isValidUrl(url)) {
            setError('Please enter a valid YouTube or Instagram video URL');
            return;
        }

        // Reset states
        setError('');
        setSuccess('');
        setLoading(true);
        setProgress(0);
        setVideoData(null);

        try {
            // Progress simulation
            const progressInterval = setInterval(() => {
                setProgress(prev => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return prev;
                    }
                    return prev + Math.random() * 15;
                });
            }, 300);

            // API call to your backend
            const response = await callDownloadAPI(url);

            // Clear progress interval and set to 100%
            clearInterval(progressInterval);
            setProgress(100);

            // Handle successful response
            if (response.success) {
                const platform = detectPlatform(url);
                const videoId = extractVideoId(url);
                // Extract data from API response
                const extractedData = response.data;
                console.log(extractedData);
                // Create comprehensive video data object
                const videoData = {
                    title: extractedData.title || `${platform.charAt(0).toUpperCase() + platform.slice(1)} ${extractedData.type}`,
                    author: extractedData.author || extractedData.username || 'Unknown User',
                    description: extractedData.description || `${platform.charAt(0).toUpperCase() + platform.slice(1)} ${extractedData.type} content`,
                    thumbnail: extractedData.thumbnail || extractedData.thumbnails?.[0] || (extractedData.media?.[0]?.thumbnail || extractedData.media?.[0]?.url) || `https://picsum.photos/400/300?random=${Date.now()}`,
                    duration: extractedData.duration || (extractedData.isVideo ? 'Unknown' : 'N/A'),
                    views: extractedData.views || 'N/A',
                    uploadDate: extractedData.uploadDate || 'Unknown',
                    quality: extractedData.quality || 'Original',
                    size: extractedData.size || 'Unknown',
                    platform: platform,
                    type: extractedData.type,
                    isVideo: extractedData.isVideo !== undefined ? extractedData.isVideo : true,
                    media: extractedData.media || [],
                    downloadUrl: extractedData.downloadUrl || extractedData.extractedUrl,
                    processingTime: response.meta?.processingTime,
                    cached: response.meta?.cached,
                    videoId: videoId
                };

                setVideoData(videoData);
                setSuccess(`${platform === 'youtube' ? 'YouTube' : 'Instagram'} ${extractedData.type || 'content'} processed successfully! Ready for download.`);
                showToastNotification('Video processed successfully!', 'success');

                // Auto-download on mobile devices
                if (window.innerWidth <= 768 && videoData.downloadUrl) {
                    setTimeout(() => {
                        downloadVideoDirectly(
                            videoData.downloadUrl,
                            `clipdart_${platform}_${videoId}.${videoData.isVideo ? 'mp4' : 'jpg'}`
                        );
                    }, 1000);
                }

            } else {
                // Handle API error response
                const errorMessage = response.error || 'Failed to process the video';
                throw new Error(errorMessage);
            }

        } catch (err) {
            console.error('Download error:', err);

            const errorMessage = err.message || 'Unknown error occurred';

            // Handle specific error types
            if (errorMessage.includes('SIGNATURE_MISMATCH') || errorMessage.includes('signature')) {
                setError('Video URL expired or signature mismatch. Please try again.');
            } else if (errorMessage.includes('private') || errorMessage.includes('Private')) {
                setError('This video is private. Only public videos can be downloaded.');
            } else if (errorMessage.includes('deleted') || errorMessage.includes('not found')) {
                setError('This video is no longer available or has been deleted.');
            } else if (errorMessage.includes('rate limit') || errorMessage.includes('429')) {
                setError('Rate limit exceeded. Please wait a moment before trying again.');
            } else if (errorMessage.includes('timeout') || errorMessage.includes('TIMEOUT')) {
                setError('Request timed out. The server might be busy. Please try again.');
            } else if (errorMessage.includes('Failed to fetch') || errorMessage.includes('Network')) {
                setError('Cannot connect to the download server. Please check your connection or try again later.');
            } else if (errorMessage.includes('Unsupported platform')) {
                setError('This platform is not supported. Only YouTube and Instagram URLs are supported.');
            } else if (errorMessage.includes('Invalid URL')) {
                setError('Please enter a valid YouTube or Instagram video URL.');
            } else if (errorMessage.includes('stories') && errorMessage.includes('expired')) {
                setError('Instagram story has expired or is no longer available.');
            } else if (errorMessage.includes('extraction failed')) {
                setError('Failed to extract video data. The video might be protected or unavailable.');
            } else if (errorMessage.includes('CORS')) {
                setError('Cross-origin request blocked. Please try again or contact support.');
            } else {
                setError(`Download failed: ${errorMessage}`);
            }

            showToastNotification('Download failed. Please try again.', 'danger');

        } finally {
            setLoading(false);
            setTimeout(() => setProgress(0), 1000);
        }
    };

    // Handle URL input changes
    const handleUrlChange = (e) => {
        setUrl(e.target.value);
        setError('');
        setSuccess('');
        setVideoData(null);
    };

    // Clear all states
    const handleClear = () => {
        setUrl('');
        setError('');
        setSuccess('');
        setVideoData(null);
        setProgress(0);
    };

    // Copy URL from clipboard
    const handlePasteFromClipboard = async () => {
        try {
            const text = await navigator.clipboard.readText();
            if (text && isValidUrl(text)) {
                setUrl(text);
                showToastNotification('URL pasted successfully!', 'success');
            } else {
                showToastNotification('No valid video URL found in clipboard', 'warning');
            }
        } catch (err) {
            showToastNotification('Unable to access clipboard', 'warning');
        }
    };

    // Copy URL to clipboard
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            showToastNotification('URL copied to clipboard!', 'success');
        }).catch(() => {
            showToastNotification('Failed to copy URL', 'danger');
        });
    };

    const features = [
        {
            icon: <Zap className="text-warning" size={28} />,
            title: "Lightning Fast",
            desc: "Process and download videos in seconds with optimized API",
            color: "warning"
        },
        {
            icon: <Smartphone size={28} style={{ color: '#FF1493' }} />,
            title: "Multi-Platform",
            desc: "Support for YouTube and Instagram videos in one tool",
            color: "primary"
        },
        {
            icon: <Shield size={28} style={{ color: '#FFB000' }} />,
            title: "Privacy First",
            desc: "No data stored, no tracking, completely anonymous usage",
            color: "success"
        },
        {
            icon: <Star size={28} style={{ color: '#FF1493' }} />,
            title: "Original Quality",
            desc: "Download videos in their original resolution and quality",
            color: "primary"
        },
        {
            icon: <Video size={28} style={{ color: '#FFB000' }} />,
            title: "Multiple Formats",
            desc: "Support for various video formats and quality options",
            color: "warning"
        },
        {
            icon: <Users size={28} style={{ color: '#FF1493' }} />,
            title: "No Registration",
            desc: "Start downloading immediately without any signup",
            color: "primary"
        }
    ];

    const badges = [
        {
            icon: CheckCircle,
            text: "API Integrated",
            color: "#FF1493",
            bgStart: "rgba(255, 20, 147, 0.1)",
            bgEnd: "rgba(255, 20, 147, 0.2)",
            borderColor: "rgba(255, 20, 147, 0.25)",
            shadowColor: "rgba(255, 20, 147, 0.2)"
        },
        {
            icon: Youtube,
            text: "YouTube & Instagram",
            color: "#FFB000",
            bgStart: "rgba(255, 176, 0, 0.1)",
            bgEnd: "rgba(255, 176, 0, 0.2)",
            borderColor: "rgba(255, 176, 0, 0.25)",
            shadowColor: "rgba(255, 176, 0, 0.2)"
        },
        {
            icon: Shield,
            text: "100% Safe",
            color: "#00D4AA",
            bgStart: "rgba(0, 212, 170, 0.1)",
            bgEnd: "rgba(0, 212, 170, 0.2)",
            borderColor: "rgba(0, 212, 170, 0.25)",
            shadowColor: "rgba(0, 212, 170, 0.2)"
        }
    ];

    const stats = [
        { icon: <Download size={20} />, value: downloadCount, label: "Downloads", color: "#FF1493" },
        { icon: <Clock size={20} />, value: "24/7", label: "Available", color: "#FFB000" },
        { icon: <Globe size={20} />, value: "Free", label: "Always", color: "#FF1493" },
        { icon: <CheckCircle size={20} />, value: "100%", label: "Success", color: "#FFB000" }
    ];

    const faqs = [
        {
            question: "Is ClipDart video downloader completely free?",
            answer: "Yes! ClipDart is 100% free with no hidden charges, premium features, or subscription fees. Download unlimited YouTube and Instagram videos without any cost."
        },
        {
            question: "What platforms does ClipDart support?",
            answer: "ClipDart supports YouTube videos, YouTube Shorts, Instagram Reels, and Instagram videos. We're continuously adding support for more platforms."
        },
        {
            question: "What video formats and quality do you support?",
            answer: "We support MP4 format in various qualities including HD, Full HD, and 4K (when available). The downloaded video maintains the best possible quality from the original source."
        },
        {
            question: "Do you store downloaded videos or user data?",
            answer: "No, we prioritize your privacy. We don't store any videos, URLs, or personal data. All processing happens in real-time and nothing is saved on our servers."
        },
        {
            question: "Can I download private videos?",
            answer: "No, due to platform privacy policies, only public videos can be downloaded. Private account content is protected and cannot be accessed."
        },
        {
            question: "Is ClipDart safe to use on mobile devices?",
            answer: "Absolutely! ClipDart is optimized for mobile devices and is completely safe. No malware, no ads, and no harmful content."
        }
    ];

    const instructionsData = [
        {
            step: 1,
            title: "Find Your Video",
            description: "Open YouTube or Instagram and navigate to the video you want to download",
            icon: <Search size={20} style={{ color: '#FF1493' }} />,
            details: "Browse through your feed or search for specific videos using keywords, hashtags, or channel names."
        },
        {
            step: 2,
            title: "Copy the Video URL",
            description: "Tap the share button and select 'Copy Link' option",
            icon: <Share2 size={20} style={{ color: '#FFB000' }} />,
            details: "The share button looks like a paper airplane or arrow. Tap it and choose 'Copy Link' from the menu."
        },
        {
            step: 3,
            title: "Paste URL Here",
            description: "Return to ClipDart and paste the copied URL in the input field",
            icon: <Link size={20} style={{ color: '#FF1493' }} />,
            details: "Use the paste button or manually paste the URL. Our tool will automatically detect the platform and validate the link."
        },
        {
            step: 4,
            title: "Download Your Video",
            description: "Click 'Download Video' and wait for processing to complete",
            icon: <ArrowDown size={20} style={{ color: '#FFB000' }} />,
            details: "Our API will process the video and provide a download link within seconds."
        }
    ];

    const getPlatformIcon = () => {
        if (!url) return <Video size={18} style={{ color: '#FF1493' }} />;
        const platform = detectPlatform(url);
        if (platform === 'youtube') return <Youtube size={18} style={{ color: '#FF1493' }} />;
        if (platform === 'instagram') return <Instagram size={18} style={{ color: '#FF1493' }} />;
        return <Video size={18} style={{ color: '#FF1493' }} />;
    };

    return (
        <>
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
                    
                    * {
                        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                    }
                    
                    @keyframes float {
                        0%, 100% { transform: translateY(0px); }
                        50% { transform: translateY(-20px); }
                    }
                    
                    @keyframes pulse {
                        0%, 100% { transform: scale(1); }
                        50% { transform: scale(1.05); }
                    }
                    @keyframes gradient {
                        0% { background-position: 0% 50%; }
                        50% { background-position: 100% 50%; }
                        100% { background-position: 0% 50%; }
                    }
                    .mobile-touch {
                        -webkit-tap-highlight-color: transparent;
                        -webkit-touch-callout: none;
                        -webkit-user-select: none;
                        user-select: none;
                    }
                    @media (max-width: 768px) {
                        .mobile-padding {
                            padding: 1rem !important;
                        }
                    }
                    
                    .glass-effect {
                        backdrop-filter: blur(20px);
                        background: rgba(255, 255, 255, 0.1);
                        border: 1px solid rgba(255, 255, 255, 0.2);
                    }
                    
                    .gradient-text {
                        background: linear-gradient(135deg, #FF1493, #FFB000);
                        -webkit-background-clip: text;
                        -webkit-text-fill-color: transparent;
                        background-clip: text;
                    }
                    
                    .card-hover {
                        transition: all 0.3s ease;
                    }
                    
                    .card-hover:hover {
                        transform: translateY(-10px);
                        box-shadow: 0 20px 40px rgba(255, 20, 147, 0.2) !important;
                    }
                    
                    .btn-gradient {
                        background: linear-gradient(135deg, #FF1493, #FFB000);
                        border: none;
                        transition: all 0.3s ease;
                    }
                    
                    .btn-gradient:hover {
                        background: linear-gradient(135deg, #FFB000, #FF1493);
                        transform: translateY(-2px);
                        box-shadow: 0 10px 20px rgba(255, 20, 147, 0.3);
                    }
                    
                    .animated-bg {
                        background: linear-gradient(-45deg, #FF1493, #FFB000, #9013FE, #FF6B6B);
                        background-size: 400% 400%;
                        animation: gradient 15s ease infinite;
                    }
                `}
            </style>

            <div className="min-vh-100 animated-bg position-relative">
                {/* Animated background shapes */}
                <div className="position-absolute w-100 h-100" style={{ overflow: 'hidden', zIndex: 0 }}>
                    <div
                        className="position-absolute rounded-circle opacity-20"
                        style={{
                            width: '300px',
                            height: '300px',
                            background: 'linear-gradient(45deg, #FF1493, #FFB000)',
                            top: '-150px',
                            right: '-150px',
                            animation: 'float 8s ease-in-out infinite'
                        }}
                    />
                    <div
                        className="position-absolute rounded-circle opacity-20"
                        style={{
                            width: '200px',
                            height: '200px',
                            background: 'linear-gradient(45deg, #FFB000, #FF1493)',
                            bottom: '-100px',
                            left: '-100px',
                            animation: 'float 10s ease-in-out infinite reverse'
                        }}
                    />
                    <div
                        className="position-absolute rounded-circle opacity-15"
                        style={{
                            width: '150px',
                            height: '150px',
                            background: 'linear-gradient(45deg, #9013FE, #FF1493)',
                            top: '20%',
                            left: '10%',
                            animation: 'float 12s ease-in-out infinite'
                        }}
                    />
                </div>

                {/* Navigation */}
                <Navbar expand="lg" className="position-relative py-3" style={{ zIndex: 10 }}>
                    <Container>
                        <Navbar.Brand className="text-white fw-bold d-flex align-items-center">
                            <img
                                src={logo}
                                alt="ClipDart Logo"
                                className="me-3"
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '12px',
                                    objectFit: 'cover'
                                }}
                            />
                            <span style={{ fontSize: '1.4rem', fontWeight: '800' }}>ClipDart</span>
                        </Navbar.Brand>
                        <Navbar.Toggle
                            aria-controls="basic-navbar-nav"
                            className="border-0 p-1 mobile-touch"
                            onClick={() => setShowOffcanvas(true)}
                        >
                            <Menu className="text-white" size={24} />
                        </Navbar.Toggle>
                    </Container>
                </Navbar>

                {/* Mobile Menu */}
                <Offcanvas show={showOffcanvas} onHide={() => setShowOffcanvas(false)} placement="end">
                    <Offcanvas.Header closeButton className="animated-bg text-white">
                        <Offcanvas.Title className="d-flex align-items-center" style={{ fontSize: '1.2rem', fontWeight: '700' }}>
                            <img
                                src={logo}
                                alt="ClipDart Logo"
                                className="me-3"
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '12px',
                                    objectFit: 'cover'
                                }}
                            />
                            ClipDart
                        </Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body>
                        <Nav className="flex-column">
                            <Nav.Link
                                className="d-flex align-items-center py-3 border-bottom mobile-touch"
                                onClick={() => { setShowInstructions(true); setShowOffcanvas(false); }}
                                style={{ fontSize: '1rem', fontWeight: '600', cursor: 'pointer' }}
                            >
                                <BookOpen className="me-3" size={20} style={{ color: '#FF1493' }} />
                                How to Use
                            </Nav.Link>
                            <Nav.Link
                                className="d-flex align-items-center py-3 border-bottom mobile-touch"
                                onClick={() => { setShowFAQ(true); setShowOffcanvas(false); }}
                                style={{ fontSize: '1rem', fontWeight: '600', cursor: 'pointer' }}
                            >
                                <HelpCircle className="me-3" size={20} style={{ color: '#FFB000' }} />
                                FAQ
                            </Nav.Link>
                            <Nav.Link
                                className="d-flex align-items-center py-3 border-bottom mobile-touch"
                                onClick={() => { setShowFeatures(true); setShowOffcanvas(false); }}
                                style={{ fontSize: '1rem', fontWeight: '600', cursor: 'pointer' }}
                            >
                                <Star className="me-3" size={20} style={{ color: '#FF1493' }} />
                                Features
                            </Nav.Link>
                        </Nav>
                    </Offcanvas.Body>
                </Offcanvas>

                <Container className="py-4 position-relative mobile-padding" style={{ zIndex: 5 }}>
                    {/* Hero Section */}
                    <Row className="justify-content-center text-center mb-5">
                        <Col xs={12} lg={8}>
                            <div className="mb-4">
                                <img
                                    src={logo}
                                    alt="ClipDart Logo"
                                    className="me-3"
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '12px',
                                        objectFit: 'cover'
                                    }}
                                />
                                <h1 className="fw-bold text-white mb-4" style={{
                                    fontSize: 'clamp(2rem, 6vw, 3.5rem)',
                                    fontWeight: '900',
                                    lineHeight: '1.1'
                                }}>
                                    Universal Video
                                    <br />
                                    <span className="gradient-text">Downloader</span>
                                </h1>
                                <p className="text-white mb-5 px-2" style={{
                                    fontSize: 'clamp(1rem, 3.5vw, 1.3rem)',
                                    fontWeight: '400',
                                    lineHeight: '1.6',
                                    opacity: '0.95'
                                }}>
                                    Download YouTube and Instagram videos instantly in original quality.
                                    <br className="d-none d-md-block" />
                                    Fast, free, and completely secure with ClipDart.
                                </p>
                                <div className="d-flex justify-content-center gap-3 mb-4 flex-wrap">
                                    {badges.map((badge, index) => {
                                        const IconComponent = badge.icon;
                                        return (
                                            <div
                                                key={index}
                                                className="group relative px-4 py-3 rounded-xl cursor-pointer transition-all duration-300 ease-out hover:scale-105 hover:-translate-y-1"
                                                style={{
                                                    background: `linear-gradient(135deg, ${badge.bgStart} 0%, ${badge.bgEnd} 100%)`,
                                                    backdropFilter: 'blur(20px)',
                                                    border: `1px solid ${badge.borderColor}`,
                                                    boxShadow: `
                0 4px 20px ${badge.shadowColor},
                0 1px 3px rgba(0, 0, 0, 0.1),
                inset 0 1px 0 rgba(255, 255, 255, 0.1)
              `
                                                }}
                                            >
                                                <div className="flex items-center gap-2 relative z-10">
                                                    <IconComponent
                                                        size={16}
                                                        style={{ color: badge.color }}
                                                        className="group-hover:scale-110 transition-transform duration-200"
                                                    />
                                                    <span
                                                        className="text-sm font-semibold whitespace-nowrap"
                                                        style={{
                                                            color: badge.color,
                                                            textShadow: `0 1px 2px ${badge.shadowColor}`
                                                        }}
                                                    >
                                                        {badge.text}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Main Download Form */}
                    <Row className="justify-content-center mb-5">
                        <Col xs={12} md={8} lg={6}>
                            <Card className="glass-effect shadow-lg border-0 card-hover">
                                <Card.Body className="p-4">
                                    <Form ref={formRef} onSubmit={(e) => { e.preventDefault(); handleDownload(); }}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="text-white fw-semibold mb-3 d-flex align-items-center">
                                                {getPlatformIcon()}
                                                <span className="ms-2">Video URL</span>
                                            </Form.Label>
                                            <div className="position-relative">
                                                <Form.Control
                                                    type="url"
                                                    placeholder="Paste YouTube or Instagram video URL here..."
                                                    value={url}
                                                    onChange={handleUrlChange}
                                                    className="form-control-lg border-0 shadow-sm"
                                                    style={{
                                                        borderRadius: '12px',
                                                        paddingRight: '100px',
                                                        background: 'rgba(255, 255, 255, 0.95)',
                                                        fontSize: '1rem'
                                                    }}
                                                />
                                                <div className="position-absolute" style={{ right: '8px', top: '50%', transform: 'translateY(-50%)' }}>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={handlePasteFromClipboard}
                                                        className="me-1 mobile-touch"
                                                        style={{
                                                            borderRadius: '8px',
                                                            padding: '6px 12px',
                                                            fontSize: '0.8rem',
                                                            borderColor: '#FF1493',
                                                            color: '#FF1493'
                                                        }}
                                                    >
                                                        <Copy size={14} />
                                                    </Button>
                                                    {url && (
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={handleClear}
                                                            className="mobile-touch"
                                                            style={{
                                                                borderRadius: '8px',
                                                                padding: '6px 12px',
                                                                fontSize: '0.8rem'
                                                            }}
                                                        >
                                                            <Trash2 size={14} />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </Form.Group>

                                        {error && (
                                            <Alert variant="danger" className="mb-3 border-0" style={{ borderRadius: '12px' }}>
                                                <AlertCircle size={16} className="me-2" />
                                                {error}
                                            </Alert>
                                        )}

                                        {success && (
                                            <Alert variant="success" className="mb-3 border-0" style={{ borderRadius: '12px' }}>
                                                <CheckCircle size={16} className="me-2" />
                                                {success}
                                            </Alert>
                                        )}

                                        {progress > 0 && progress < 100 && (
                                            <div className="mb-3">
                                                <div className="d-flex justify-content-between mb-2">
                                                    <small className="text-white">Processing...</small>
                                                    <small className="text-white">{Math.round(progress)}%</small>
                                                </div>
                                                <ProgressBar
                                                    now={progress}
                                                    className="mb-2"
                                                    style={{ height: '8px', borderRadius: '4px' }}
                                                />
                                            </div>
                                        )}

                                        <Button
                                            onClick={handleDownload}
                                            disabled={loading || !url.trim()}
                                            className="btn-gradient w-100 py-3 fw-bold mobile-touch"
                                            style={{
                                                borderRadius: '12px',
                                                fontSize: '1.1rem',
                                                fontWeight: '700'
                                            }}
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    Processing...
                                                </>
                                            ) : (
                                                <>
                                                    <Download size={20} className="me-2" />
                                                    Download Video
                                                </>
                                            )}
                                        </Button>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Video Preview Card */}
                    {videoData && (
                        <Row className="justify-content-center mb-5">
                            <Col xs={12} md={10} lg={8}>
                                <Card className="glass-effect border-0 shadow-lg card-hover">
                                    <Card.Body className="p-4">
                                        <Row>
                                            <Col xs={12}>
                                                <div className="d-flex align-items-center mb-3">
                                                    <div className="me-3">
                                                        {videoData.platform === 'youtube' ? (
                                                            <Youtube size={24} style={{ color: '#FF1493' }} />
                                                        ) : (
                                                            <Instagram size={24} style={{ color: '#FF1493' }} />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h6 className="text-white fw-bold mb-1" style={{ fontSize: '1.1rem' }}>
                                                            {videoData.title}
                                                        </h6>
                                                        <div className="text-white-50 small d-flex flex-wrap gap-3">
                                                            <span><Clock size={14} className="me-1" />{videoData.duration}</span>
                                                            <span><User size={14} className="me-1" />{videoData.author}</span>
                                                            {videoData.media && videoData.media.length > 1 && (
                                                                <span><Video size={14} className="me-1" />{videoData.media.length} items</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Media Gallery for Multiple Items */}
                                                {videoData.media && videoData.media.length > 1 ? (
                                                    <div className="mb-4">
                                                        <h6 className="text-white fw-semibold mb-3">
                                                            Media Gallery ({videoData.media.length} items)
                                                        </h6>
                                                        <Row className="g-3">
                                                            {videoData.media.map((mediaItem, index) => (
                                                                <Col xs={6} sm={4} md={3} key={index}>
                                                                    <Card className="bg-dark border-0 h-100">
                                                                        <div className="position-relative">
                                                                            <img
                                                                                src={mediaItem.thumbnail || mediaItem.url}
                                                                                alt={`Media ${index + 1}`}
                                                                                className="img-fluid rounded-top"
                                                                                style={{
                                                                                    width: '100%',
                                                                                    aspectRatio: '1/1',
                                                                                    objectFit: 'cover'
                                                                                }}
                                                                                onError={(e) => {
                                                                                    e.target.src = `https://picsum.photos/200/200?random=${index}`;
                                                                                }}
                                                                            />
                                                                            <div className="position-absolute top-0 end-0 m-2">
                                                                                <div
                                                                                    className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                                                                                    style={{
                                                                                        width: '24px',
                                                                                        height: '24px',
                                                                                        background: 'rgba(255, 20, 147, 0.8)',
                                                                                        fontSize: '0.7rem'
                                                                                    }}
                                                                                >
                                                                                    {index + 1}
                                                                                </div>
                                                                            </div>
                                                                            {mediaItem.type === 'video' && (
                                                                                <div className="position-absolute bottom-2 start-2">
                                                                                    <div
                                                                                        className="rounded-pill px-2 py-1 d-flex align-items-center"
                                                                                        style={{
                                                                                            background: 'rgba(0, 0, 0, 0.7)',
                                                                                            fontSize: '0.7rem'
                                                                                        }}
                                                                                    >
                                                                                        <Video size={12} className="text-white me-1" />
                                                                                        <span className="text-white">Video</span>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <Card.Body className="p-2 text-center">
                                                                            <Button
                                                                                size="sm"
                                                                                onClick={() => downloadVideoDirectly(
                                                                                    mediaItem.url,
                                                                                    `clipdart_${videoData.platform}_${videoData.type}_${index + 1}.${mediaItem.type === 'video' ? 'mp4' : 'jpg'}`
                                                                                )}
                                                                                className="btn-gradient w-100 mobile-touch"
                                                                                style={{
                                                                                    borderRadius: '6px',
                                                                                    fontSize: '0.75rem',
                                                                                    fontWeight: '600',
                                                                                    padding: '4px 8px'
                                                                                }}
                                                                            >
                                                                                <Download size={12} className="me-1" />
                                                                                Download
                                                                            </Button>
                                                                        </Card.Body>
                                                                    </Card>
                                                                </Col>
                                                            ))}
                                                        </Row>

                                                        {/* Bulk Download Button */}
                                                        <div className="mt-3 text-center">
                                                            <Button
                                                                onClick={() => {
                                                                    videoData.media.forEach((mediaItem, index) => {
                                                                        setTimeout(() => {
                                                                            downloadVideoDirectly(
                                                                                mediaItem.url,
                                                                                `clipdart_${videoData.platform}_${videoData.type}_${index + 1}.${mediaItem.type === 'video' ? 'mp4' : 'jpg'}`
                                                                            );
                                                                        }, index * 1000); // Stagger downloads by 1 second
                                                                    });
                                                                    showToastNotification(`Downloading ${videoData.media.length} items...`, 'info');
                                                                }}
                                                                className="btn-gradient mobile-touch"
                                                                style={{
                                                                    borderRadius: '25px',
                                                                    fontSize: '0.9rem',
                                                                    fontWeight: '600',
                                                                    padding: '8px 20px'
                                                                }}
                                                            >
                                                                <Download size={16} className="me-2" />
                                                                Download All ({videoData.media.length})
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    // Single Media Item (existing layout)
                                                    <Row className="mb-3">
                                                        <Col xs={4} md={3}>
                                                            <img
                                                                src={videoData?.thumbnail}
                                                                alt="Video thumbnail"
                                                                className="img-fluid rounded"
                                                                style={{
                                                                    width: '100%',
                                                                    aspectRatio: '16/9',
                                                                    objectFit: 'cover',
                                                                    borderRadius: '8px'
                                                                }}
                                                            />
                                                        </Col>
                                                        <Col xs={8} md={9}>
                                                            <div className="d-flex gap-2 flex-wrap">
                                                                <Button
                                                                    onClick={() => downloadVideoDirectly(
                                                                        videoData.downloadUrl,
                                                                        `clipdart_${videoData.platform}_${videoData.videoId}.${videoData.isVideo ? 'mp4' : 'jpg'}`
                                                                    )}
                                                                    className="btn-gradient flex-fill mobile-touch"
                                                                    style={{
                                                                        borderRadius: '8px',
                                                                        fontSize: '0.9rem',
                                                                        fontWeight: '600'
                                                                    }}
                                                                >
                                                                    <Download size={16} className="me-2" />
                                                                    Download
                                                                </Button>
                                                                <Button
                                                                    variant="outline-light"
                                                                    onClick={() => copyToClipboard(videoData.downloadUrl)}
                                                                    className="mobile-touch"
                                                                    style={{
                                                                        borderRadius: '8px',
                                                                        fontSize: '0.9rem'
                                                                    }}
                                                                >
                                                                    <Copy size={16} />
                                                                </Button>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                )}

                                                {/* Additional Info */}
                                                {videoData.processingTime && (
                                                    <div className="text-white-50 small text-center">
                                                        <Info size={12} className="me-1" />
                                                        Processed in {videoData.processingTime}
                                                        {videoData.cached && <span className="ms-2">(Cached)</span>}
                                                    </div>
                                                )}
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Statistics */}
                    <Row className="justify-content-center mb-5">
                        <Col xs={12} lg={8}>
                            <Row className="g-3">
                                {stats.map((stat, index) => (
                                    <Col xs={6} md={3} key={index}>
                                        <Card className="glass-effect border-0 text-center card-hover">
                                            <Card.Body className="py-3">
                                                <div className="mb-2" style={{ color: stat.color }}>
                                                    {stat.icon}
                                                </div>
                                                <h4 className="text-white fw-bold mb-1" style={{ fontSize: '1.5rem' }}>
                                                    {stat.value}
                                                </h4>
                                                <small className="text-white-50">{stat.label}</small>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        </Col>
                    </Row>

                    {/* Quick Access Buttons */}
                    <Row className="justify-content-center mb-5">
                        <Col xs={12} md={8}>
                            <div className="d-flex gap-3 justify-content-center flex-wrap">
                                <Button
                                    variant="outline-light"
                                    onClick={() => setShowInstructions(true)}
                                    className="d-flex align-items-center mobile-touch"
                                    style={{
                                        borderRadius: '25px',
                                        padding: '10px 20px',
                                        borderColor: 'rgba(255, 255, 255, 0.3)',
                                        backdropFilter: 'blur(10px)'
                                    }}
                                >
                                    <BookOpen size={18} className="me-2" />
                                    How to Use
                                </Button>
                                <Button
                                    variant="outline-light"
                                    onClick={() => setShowFAQ(true)}
                                    className="d-flex align-items-center mobile-touch"
                                    style={{
                                        borderRadius: '25px',
                                        padding: '10px 20px',
                                        borderColor: 'rgba(255, 255, 255, 0.3)',
                                        backdropFilter: 'blur(10px)'
                                    }}
                                >
                                    <HelpCircle size={18} className="me-2" />
                                    FAQ
                                </Button>
                                <Button
                                    variant="outline-light"
                                    onClick={() => setShowFeatures(true)}
                                    className="d-flex align-items-center mobile-touch"
                                    style={{
                                        borderRadius: '25px',
                                        padding: '10px 20px',
                                        borderColor: 'rgba(255, 255, 255, 0.3)',
                                        backdropFilter: 'blur(10px)'
                                    }}
                                >
                                    <Star size={18} className="me-2" />
                                    Features
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Container>

                {/* Instructions Modal */}
                <Modal show={showInstructions} onHide={() => setShowInstructions(false)} size="lg" centered>
                    <Modal.Header closeButton className="animated-bg text-white">
                        <Modal.Title className="d-flex align-items-center">
                            <BookOpen size={24} className="me-2" />
                            How to Download Videos
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        <Row className="g-4">
                            {instructionsData.map((instruction) => (
                                <Col xs={12} md={6} key={instruction.step}>
                                    <Card className="h-100 border-0 shadow-sm">
                                        <Card.Body className="text-center p-4">
                                            <div className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                                                style={{
                                                    width: '60px',
                                                    height: '60px',
                                                    background: 'linear-gradient(135deg, #FF1493, #FFB000)'
                                                }}>
                                                {instruction.icon}
                                            </div>
                                            <div className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center text-white fw-bold"
                                                style={{
                                                    width: '30px',
                                                    height: '30px',
                                                    background: 'linear-gradient(135deg, #FF1493, #FFB000)',
                                                    fontSize: '1rem'
                                                }}>
                                                {instruction.step}
                                            </div>
                                            <h6 className="fw-bold mb-2">{instruction.title}</h6>
                                            <p className="text-muted mb-2">{instruction.description}</p>
                                            <small className="text-muted">{instruction.details}</small>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Modal.Body>
                </Modal>

                {/* FAQ Modal */}
                <Modal show={showFAQ} onHide={() => setShowFAQ(false)} size="lg" centered>
                    <Modal.Header closeButton className="animated-bg text-white">
                        <Modal.Title className="d-flex align-items-center">
                            <HelpCircle size={24} className="me-2" />
                            Frequently Asked Questions
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-0">
                        <Accordion flush>
                            {faqs.map((faq, index) => (
                                <Accordion.Item eventKey={index.toString()} key={index}>
                                    <Accordion.Header className="fw-semibold">
                                        {faq.question}
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        {faq.answer}
                                    </Accordion.Body>
                                </Accordion.Item>
                            ))}
                        </Accordion>
                    </Modal.Body>
                </Modal>

                {/* Features Modal */}
                <Modal show={showFeatures} onHide={() => setShowFeatures(false)} size="lg" centered>
                    <Modal.Header closeButton className="animated-bg text-white">
                        <Modal.Title className="d-flex align-items-center">
                            <Star size={24} className="me-2" />
                            ClipDart Features
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        <Row className="g-4">
                            {features.map((feature, index) => (
                                <Col xs={12} md={6} key={index}>
                                    <Card className="h-100 border-0 shadow-sm card-hover">
                                        <Card.Body className="p-4">
                                            <div className="d-flex align-items-start">
                                                <div className="me-3">
                                                    {feature.icon}
                                                </div>
                                                <div>
                                                    <h6 className="fw-bold mb-2">{feature.title}</h6>
                                                    <p className="text-muted mb-0">{feature.desc}</p>
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Modal.Body>
                </Modal>

                {/* Toast Notifications */}
                <ToastContainer position="bottom-end" className="p-3">
                    <Toast
                        show={showToast}
                        onClose={() => setShowToast(false)}
                        delay={3000}
                        autohide
                        className="border-0"
                    >
                        <Toast.Header className={`bg-${toastVariant} text-white border-0`}>
                            <strong className="me-auto">ClipDart</strong>
                        </Toast.Header>
                        <Toast.Body className={`bg-${toastVariant === 'success' ? 'success' : toastVariant === 'danger' ? 'danger' : 'warning'} text-white`}>
                            {toastMessage}
                        </Toast.Body>
                    </Toast>
                </ToastContainer>

                {/* Footer */}
                <footer className="text-center py-4 position-relative  text-white-50" style={{ zIndex: 5 }}>
                    <Container>
                        <div className="small">
                            <p className="mb-2">
                                © {currentYear} ClipDart. Fast, free, and secure video downloads.
                            </p>
                            <div className="d-flex justify-content-center gap-3 flex-wrap mb-2">
                                <span className="d-flex align-items-center">
                                    <Wifi size={14} className="me-1" />
                                    Always Online
                                </span>
                                <span className="d-flex align-items-center">
                                    <Shield size={14} className="me-1" />
                                    100% Safe
                                </span>
                                <span className="d-flex align-items-center">
                                    <Globe size={14} className="me-1" />
                                    No Registration
                                </span>
                            </div>
                            <p className="mb-0 text-white-50">
                                We are not affiliated with Instagram or Meta.
                            </p>
                        </div>
                    </Container>
                </footer>
            </div>
        </>
    );
};

export default ClipDart;